import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import connect from '../utils/connect';

import { Button, LoadingDots } from '@app/ui';

import sendNotification from '@app/actions/notifications/send';
import isHandledBySportsApi from '@app/utils/isHandledBySportsApi';
import sportsApi from '@app/utils/sportsApi';
import prepareRenderList from '../utils/prepareRenderList';
import combineActions from '../utils/combineActions';
import SocialMediaLinkageCard from './SocialMediaLinkageCard';

const TWITTER_PLATFORM_ID = 1;

// eslint-disable-next-line @typescript-eslint/no-var-requires
const twitterLogo = require('../images/social_twitter.png');

const getAuthUrl = async () => {
    const basepath = __BASEPATH__ ? `/${__BASEPATH__}` : '';
    return sportsApi
        .url('/platforms/1/oauth-request-tokens')
        .query({
            callbackUrl:
                window.location.origin + basepath + '/authentication/twitter',
        })
        .get()
        .json();
};

const hasAuthorizationUrl = response =>
    typeof response?.authorizationUrl === 'string' &&
    response.authorizationUrl.startsWith('http');

function TwitterAuthentication({ client, sendNotification }) {
    const { t } = useTranslation('settings');
    const [isLoadingRedirect, setLoadingRedirect] = useState(false);
    const [isLoadingList, setLoadingList] = useState(true);
    const [accounts, setAccounts] = useState([]);

    useEffect(() => {
        (async () => {
            const clientId = client.id;
            const list = await sportsApi
                .url(`/clients/${clientId}/authorized-accounts`)
                .query({ platformId: TWITTER_PLATFORM_ID })
                .get()
                .json();

            setLoadingList(false);
            setAccounts(list);
        })();
    }, []);

    const openRedirect = async () => {
        setLoadingRedirect(true);

        try {
            const res = await getAuthUrl();

            if (!hasAuthorizationUrl(res)) {
                if (!isHandledBySportsApi(res)) {
                    sendNotification(
                        t(
                            'clientConfiguration.social.twitterRedirect.somethingWrong',
                        ),
                        { context: 'danger' },
                    );
                }
                return;
            }

            const resStr = JSON.stringify(res);

            localStorage.setItem('twitter-auth', resStr);
            localStorage.setItem('twitter-auth-client-id', client.id);

            location.replace(res.authorizationUrl);
        } catch {
            sendNotification(
                t('clientConfiguration.social.twitterRedirect.somethingWrong'),
                { context: 'danger' },
            );
        } finally {
            setLoadingRedirect(false);
        }
    };

    const onUnlink = async accountId => {
        const clientId = client.id;

        setLoadingList(true);

        await sportsApi
            .url(`/clients/${clientId}/authorized-accounts/${accountId}`)
            .delete()
            .res();

        const updatedList = accounts.filter(
            clientAuthorizedAccount =>
                clientAuthorizedAccount.account.id !== accountId,
        );
        setAccounts(updatedList);
        setLoadingList(false);
    };

    const renderList = prepareRenderList(accounts);

    return (
        <div className="_flex-card">
            <div className="gds-card -m-b-0">
                {accounts.length > 5 && <span className="-shadow" />}

                <div className="gds-card__block gds-card__block--divide-bottom -p-h-3">
                    <p className="gds-text--regular">
                        <i className="fa fab-fa6 fa-x-twitter -m-r-2" />
                        {t('clientConfiguration.social.twitter.title')}
                    </p>
                </div>
                <div className="gds-card__block -p-h-3 -p-t-3 -p-b-1 -overflow-y-scroll -social-account-container">
                    <div className="gds-flex" style={{ height: '100%' }}>
                        {isLoadingList && <LoadingDots />}

                        {!isLoadingList && (
                            <div
                                className="gds-flex gds-flex--justify-center -m-b-2"
                                style={{ width: '100%' }}
                            >
                                <Button
                                    onClick={openRedirect}
                                    variant="primary"
                                    size="small"
                                    disabled={isLoadingRedirect}
                                    style={{ width: '100%' }}
                                >
                                    {accounts.length > 0
                                        ? t(
                                              'clientConfiguration.social.twitter.linkAnother',
                                          )
                                        : t(
                                              'clientConfiguration.social.twitter.link',
                                          )}
                                </Button>
                            </div>
                        )}

                        {renderList.map(clientAuthorizedAccount => {
                            const account = clientAuthorizedAccount.account;
                            const revoked = clientAuthorizedAccount.revoked;

                            const accountId = account.id;
                            const profilePictureUrl = account.profilePictureUrl;
                            const name = account.name;

                            const _onUnlink = () => onUnlink(accountId);

                            return (
                                <SocialMediaLinkageCard
                                    key={accountId}
                                    type="twitter"
                                    logo={profilePictureUrl}
                                    revoked={revoked}
                                    _link={openRedirect}
                                    _unlink={_onUnlink}
                                    name={name}
                                    altLogo={twitterLogo}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

TwitterAuthentication.propTypes = {
    client: PropTypes.object.isRequired,
    sendNotification: PropTypes.func.isRequired,
};

export default connect(
    ['client'],
    combineActions({ sendNotification }),
)(TwitterAuthentication);
